import { ReactElement, useMemo } from 'react'
import { allElementTypes } from '@scene'
import { FloorplanMesh } from '@modugen/ui-toolkit'
import { useControlStore, useModelStore } from '@editorStores'
import { useStoreyToElementTypeAndZTranslation } from '@editorHooks'

/** Dark blue of the proposal walls, clearly distinct from the regular floorplan colors. */
const PROPOSAL_COLOR = '#00008b'

const PROPOSAL_OPACITY = 0.2

/** Walls are drawn thicker than the regular floorplan walls (linewidth 2). */
const PROPOSAL_LINEWIDTHS: Record<'purlins' | 'beams' | 'inner_walls' | 'outer_walls', number> = {
  inner_walls: 8,
  outer_walls: 8,
  beams: 2,
  purlins: 2,
}

/** Extra z-offset so the proposal renders above the regular floorplan walls. */
const PROPOSAL_Z_OFFSET = 0.02

// The proposal is display-only; clicks stay with the regular floorplan mesh underneath.
const NOTHING_SELECTABLE = Object.fromEntries(allElementTypes.map(type => [type, false])) as Record<
  ElementTypes,
  boolean
>

interface Props {
  /** Adjusted copies of the walls that can be connected, from `computeWallConnectionProposals`. */
  proposalWalls: Wall[]
}

/**
 * Scene overlay of the "Automatisch" wall-connect tool: renders the proposed (extended/shortened)
 * walls as a second, walls-only floorplan mesh — dark blue, thicker and semi-transparent, slightly
 * above the regular floorplan mesh — so the differences to the current model stand out.
 */
const WallConnectionProposalMesh = ({ proposalWalls }: Props): ReactElement | null => {
  const storey = useControlStore(state => state.floorplanState.storey)
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)

  const baseZTranslation = useStoreyToElementTypeAndZTranslation(storey)

  const storeyToElementTypeAndZTranslation = useMemo(() => {
    if (!baseZTranslation) return undefined
    return Object.fromEntries(
      Object.entries(baseZTranslation).map(([storeyKey, config]) => [
        storeyKey,
        {
          ...config,
          inner_walls: (config.inner_walls ?? 0) + PROPOSAL_Z_OFFSET,
          outer_walls: (config.outer_walls ?? 0) + PROPOSAL_Z_OFFSET,
        },
      ]),
    )
  }, [baseZTranslation])

  const modelData = useMemo<PlanarModel>(
    () => ({
      walls: proposalWalls,
      slabs: [],
      roof_slabs: [],
      beams: [],
      purlins: [],
      columns: [],
      vertical_slabs: [],
      vertical_roof_slabs: [],
      rips: [],
      lintels: [],
      foundationElements: [],
      storey_boundaries: storeyBoundaries,
    }),
    [proposalWalls, storeyBoundaries],
  )

  const guidToColor = useMemo(
    () => Object.fromEntries(proposalWalls.map(wall => [wall.guid, PROPOSAL_COLOR])),
    [proposalWalls],
  )

  const storeyToElementTypeAndOpacity = useMemo(
    () =>
      storey
        ? { [storey]: { inner_walls: PROPOSAL_OPACITY, outer_walls: PROPOSAL_OPACITY } }
        : undefined,
    [storey],
  )

  if (!storey || proposalWalls.length === 0) return null

  return (
    <FloorplanMesh
      modelData={modelData}
      guidToColor={guidToColor}
      storeyToElementTypeAndOpacity={storeyToElementTypeAndOpacity}
      storeyToElementTypeAndZTranslation={storeyToElementTypeAndZTranslation}
      showInnerWalls
      showOuterWalls
      showBeams={false}
      showPurlins={false}
      showSlabs={false}
      showRoofSlabs={false}
      showVerticalSlabs={false}
      showVerticalRoofSlabs={false}
      showColumns={false}
      showRips={false}
      typeToSelectable={NOTHING_SELECTABLE}
      elementTypeToLinewidth={PROPOSAL_LINEWIDTHS}
    />
  )
}

export default WallConnectionProposalMesh
